import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Video, Calendar, Clock, ExternalLink, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

function ensureHttps(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return "https://" + url;
}

export default function AdminLive() {
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState({ title: "", course_id: "", scheduled_date: "", scheduled_time: "", meet_link: "", duration_minutes: 60, description: "", status: "upcoming" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const [sessRes, coursesRes] = await Promise.all([
      api.get("/admin/all-live-sessions"),
      api.get("/courses"),
    ]);
    setSessions(sessRes.data);
    setCourses(coursesRes.data);
  };

  const resetForm = () => {
    setForm({ title: "", course_id: "", scheduled_date: "", scheduled_time: "", meet_link: "", duration_minutes: 60, description: "", status: "upcoming" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.course_id || !form.title || !form.meet_link || !form.scheduled_date || !form.scheduled_time) {
      alert("Please fill all required fields.");
      return;
    }
    const payload = { ...form, meet_link: ensureHttps(form.meet_link) };
    if (editing) {
      await api.put(`/live-sessions/${editing}`, payload);
    } else {
      await api.post(`/courses/${form.course_id}/live-sessions`, payload);
    }
    resetForm();
    fetchData();
  };

  const handleEdit = (s) => {
    setForm({
      title: s.title || "", course_id: s.course_id || "", scheduled_date: s.scheduled_date || "",
      scheduled_time: s.scheduled_time || "", meet_link: s.meet_link || "", duration_minutes: s.duration_minutes || 60,
      description: s.description || "", status: s.status || "upcoming",
    });
    setEditing(s.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this live session?")) {
      await api.delete(`/live-sessions/${id}`);
      fetchData();
    }
  };

  const handleCopy = (link, id) => {
    navigator.clipboard.writeText(ensureHttps(link));
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatus = (s) => {
    const now = new Date();
    const sessionTime = new Date(`${s.scheduled_date}T${s.scheduled_time}`);
    const endTime = new Date(sessionTime.getTime() + (s.duration_minutes || 60) * 60000);
    if (s.status === "cancelled") return "cancelled";
    if (now >= sessionTime && now <= endTime) return "live";
    if (now > endTime) return "completed";
    return "upcoming";
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none admin-input";

  return (
    <AdminLayout>
      <div data-testid="admin-live-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Live Sessions</h1>
            <p className="text-xs text-slate-500">Schedule and manage Google Meet live classes</p>
          </div>
        </div>

        <button data-testid="create-live-btn" onClick={() => { resetForm(); setShowForm(true); }} className="w-full bg-[#0284C7] text-white font-bold py-3 text-sm flex items-center justify-center gap-2 mb-4 rounded-lg">
          <Plus size={16} /> Create New Live Session
        </button>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border rounded-lg p-4 mb-4 space-y-3" data-testid="live-form">
            <h3 className="text-sm font-bold admin-text">{editing ? "Edit Session" : "Create New Live Session"}</h3>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase">Course *</label>
              <select data-testid="live-course" className={inputClass} value={form.course_id} onChange={e => setForm({ ...form, course_id: e.target.value })} required>
                <option value="">Select Course</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase">Title *</label>
              <input data-testid="live-title" className={inputClass} placeholder="e.g., Grammar Doubt Session" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Date *</label>
                <input data-testid="live-date" type="date" className={inputClass} value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Time *</label>
                <input data-testid="live-time" type="time" className={inputClass} value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} required />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase">Google Meet Link *</label>
              <input data-testid="live-meet-link" className={inputClass} placeholder="https://meet.google.com/xxx-xxxx-xxx" value={form.meet_link} onChange={e => setForm({ ...form, meet_link: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Duration (min)</label>
                <input type="number" className={inputClass} value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Status</label>
                <select className={inputClass} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="upcoming">Upcoming</option>
                  <option value="live">Live Now</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase">Description</label>
              <textarea className={inputClass} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description (optional)" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" data-testid="save-live-btn" className="flex-1 bg-[#0284C7] text-white font-bold py-2.5 text-sm rounded-lg">{editing ? "Update" : "Create"}</button>
              <button type="button" onClick={resetForm} className="px-4 py-2.5 text-sm font-bold text-slate-500 border border-slate-300 rounded-lg">Cancel</button>
            </div>
          </form>
        )}

        {/* Sessions List */}
        {sessions.length > 0 ? (
          <div className="space-y-3">
            {sessions.map(s => {
              const status = getStatus(s);
              return (
                <div key={s.id} className="admin-card border rounded-lg p-4" data-testid={`live-item-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          status === "live" ? "bg-red-500 text-white" :
                          status === "completed" ? "bg-slate-400 text-white" :
                          status === "cancelled" ? "bg-orange-400 text-white" :
                          "bg-green-500 text-white"
                        }`}>{status === "live" ? "LIVE NOW" : status.toUpperCase()}</span>
                        <span className="text-[10px] text-slate-500 truncate">{s.course_title}</span>
                      </div>
                      <h3 className="text-sm font-bold admin-text">{s.title}</h3>
                      {s.description && <p className="text-xs admin-text-secondary mt-0.5">{s.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs admin-text-secondary">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {s.scheduled_date}</span>
                        <span className="flex items-center gap-1"><Clock size={12} /> {s.scheduled_time}</span>
                        <span>{s.duration_minutes} min</span>
                      </div>
                      {s.meet_link && (
                        <div className="flex items-center gap-2 mt-2">
                          <a
                            href={ensureHttps(s.meet_link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#0284C7] font-bold hover:underline"
                            data-testid={`meet-link-${s.id}`}
                          >
                            <ExternalLink size={12} /> {s.meet_link}
                          </a>
                          <button onClick={() => handleCopy(s.meet_link, s.id)} className="p-1 text-slate-400" data-testid={`copy-link-${s.id}`}>
                            {copied === s.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => handleEdit(s)} className="p-2 text-[#0284C7] bg-[#0284C7]/10 rounded" data-testid={`edit-live-${s.id}`}><Edit size={14} /></button>
                      <button onClick={() => handleDelete(s.id)} className="p-2 text-red-500 bg-red-500/10 rounded" data-testid={`delete-live-${s.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : !showForm && (
          <div className="admin-card border rounded-lg p-8 text-center">
            <Video size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm admin-text-secondary">No live sessions yet.</p>
            <p className="text-xs text-slate-400 mt-1">Click "Create New Live Session" to schedule your first class.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
